import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	DuelChipStackPng,
	DuelIconCardsSvg,
	DuelIconChevronSvg,
	DuelIconCrownSvg,
	DuelStarPng,
	DuelTypeMegaPng,
	DuelTypeRankedPng,
	DuelTypeSpecialPng,
	DuelXpJpg,
} from '../../assets/duels';
import type { DuelHistoryEntryDto } from '../../services/api';
import { duelsHistoryLoadMore, duelsHistoryRequest } from '../../store/duels/duelsActions';
import { DuelHistoryTab, selectDuelsHistory } from '../../store/duels/duelsReducer';
import { TouchableOpacity } from '../Components/common/Touchable';
import {
	asRewardStatus,
	DuelRewardStatusPill,
	rewardStatusText,
	rewardTitle,
} from '../Components/Duels/DuelRewardUi';
import { DuelStatTile } from '../Components/Duels/DuelStatCards';
import { duelText } from '../Components/Duels/duelsText';
import { divisionBadge, formatScore, formatSettledAt } from '../Components/Duels/duelsTheme';
import {
	DuelButton,
	DuelLoading,
	DuelNotice,
	DuelPanel,
	DuelsPage,
	duelTextStyles,
} from '../Components/Duels/DuelsUi';
import { eventColors } from '../Components/Event/eventTheme';
import { RootStackParamList } from '../navigator';

const duelTypeIcon = (name: string | null | undefined) => {
	const normalized = (name ?? '').trim().toLowerCase();
	if (normalized.startsWith('special')) return DuelTypeSpecialPng;
	if (normalized.startsWith('mega')) return DuelTypeMegaPng;
	return DuelTypeRankedPng;
};

type NavType = NativeStackNavigationProp<RootStackParamList>;

const TABS: { tab: DuelHistoryTab; key: string }[] = [
	{ tab: 'all', key: 'T_SITE_DUELS_TAB_ALL' },
	{ tab: 'current', key: 'T_SITE_DUELS_TAB_THIS_SEASON' },
	{ tab: 'previous', key: 'T_SITE_DUELS_TAB_PREVIOUS_SEASON' },
];

const HistoryRow = ({
	entry,
	width,
	scale,
	open,
	onToggle,
	onBreakdown,
}: {
	entry: DuelHistoryEntryDto;
	width: number;
	scale: number;
	open: boolean;
	onToggle: () => void;
	onBreakdown: () => void;
}) => {
	const { t } = useTranslation();
	const placement = entry.placementReward;
	const milestones = entry.milestones
		.map(milestone => ({ milestone, title: rewardTitle(t, milestone) }))
		.filter(({ title }) => !!title);
	const inner = width - 24 * scale;
	const tileWidth = (inner - 8 * scale) / 2;
	const nowText = (after: number | null) =>
		after === null ? null : duelText(t, 'T_SITE_DUELS_NOW', [formatScore(after)]);

	return (
		<DuelPanel scale={scale} width={width} highlighted={open}>
			<TouchableOpacity
				onPress={onToggle}
				accessibilityRole='button'
				accessibilityState={{ expanded: open }}
				style={[styles.row, { columnGap: 8 * scale }]}
			>
				<View style={[styles.flex, { rowGap: 4 * scale }]}>
					<Text numberOfLines={1} style={[duelTextStyles.body, { fontSize: 10 * scale }]}>
						{formatSettledAt(entry.settledAt)}
					</Text>
					<View style={[styles.row, { columnGap: 6 * scale }]}>
						<Image
							source={duelTypeIcon(entry.duelTypeName)}
							style={{ width: 28 * scale, height: 28 * scale }}
							resizeMode='contain'
						/>
						<View style={styles.flex}>
							<Text numberOfLines={1} style={[duelTextStyles.title, { fontSize: 13 * scale }]}>
								{entry.duelTypeName ?? duelText(t, 'T_SITE_DUELS_TITLE')}
							</Text>
							{open ? (
								<Text style={[duelTextStyles.body, { fontSize: 11 * scale }]}>
									{duelText(t, 'T_SITE_DUELS_PLAYERS', [entry.eligibleCount])}
								</Text>
							) : null}
						</View>
					</View>
				</View>
				<View style={[styles.divider, { height: 36 * scale }]} />
				<View style={{ width: 64 * scale }}>
					{open ? (
						<Text style={[duelTextStyles.body, styles.centerText, { fontSize: 10 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_SCORE')}
						</Text>
					) : null}
					<Text style={[duelTextStyles.title, styles.centerText, { fontSize: 18 * scale }]}>
						{formatScore(entry.displayScore)}
					</Text>
				</View>
				<View style={[styles.divider, { height: 36 * scale }]} />
				<View style={{ width: 52 * scale }}>
					{open ? (
						<Text style={[duelTextStyles.body, styles.centerText, { fontSize: 10 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_POSITION')}
						</Text>
					) : null}
					<View style={[styles.row, styles.centerRow, { columnGap: 3 * scale }]}>
						<Text style={[duelTextStyles.gold, { fontSize: 20 * scale }]}>
							{entry.rank ? `#${entry.rank}` : '—'}
						</Text>
						{entry.rank === 1 ? <DuelIconCrownSvg width={14 * scale} height={12 * scale} /> : null}
					</View>
				</View>
				<View style={{ transform: [{ rotate: open ? '-90deg' : '90deg' }] }}>
					<DuelIconChevronSvg width={14 * scale} height={14 * scale} />
				</View>
			</TouchableOpacity>

			{open ? (
				<>
					<View style={[styles.row, { columnGap: 8 * scale }]}>
						<DuelStatTile
							icon={divisionBadge(entry.divisionAfter?.split(' ')[0])}
							label={duelText(t, 'T_SITE_DUELS_DUEL_RATING')}
							delta={entry.ratingDelta}
							nowText={nowText(entry.ratingAfter)}
							width={tileWidth}
							scale={scale}
						/>
						<DuelStatTile
							icon={DuelStarPng}
							label={duelText(t, 'T_SITE_DUELS_SEASON_POINTS')}
							delta={entry.seasonPointsDelta}
							nowText={nowText(entry.seasonPointsAfter)}
							width={tileWidth}
							scale={scale}
						/>
					</View>

					{placement?.chips || placement?.experience ? (
						<View style={[styles.row, { columnGap: 12 * scale }]}>
							<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
								{duelText(t, 'T_SITE_DUELS_REWARDS')}
							</Text>
							{placement.chips ? (
								<View style={[styles.row, { columnGap: 6 * scale }]}>
									<Image
										source={DuelChipStackPng}
										style={{ width: 28 * scale, height: 28 * scale }}
										resizeMode='contain'
									/>
									<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
										{duelText(t, 'T_SITE_DUELS_REWARD_CHIPS', [formatScore(placement.chips)])}
									</Text>
								</View>
							) : null}
							{placement.experience ? (
								<View style={[styles.row, { columnGap: 6 * scale }]}>
									<Image
										source={DuelXpJpg}
										style={{ width: 24 * scale, height: 24 * scale }}
										resizeMode='contain'
									/>
									<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
										{duelText(t, 'T_SITE_DUELS_REWARD_XP', [formatScore(placement.experience)])}
									</Text>
								</View>
							) : null}
						</View>
					) : null}

					{milestones.map(({ milestone, title }) => {
						const status =
							milestone.rewardType === 'RealWorld' ? asRewardStatus(milestone.status) : null;
						return (
							<View
								key={milestone.milestoneId}
								style={[styles.row, styles.between, { columnGap: 8 * scale }]}
							>
								<View style={[styles.row, styles.flex, { columnGap: 6 * scale }]}>
									<DuelIconCrownSvg width={14 * scale} height={12 * scale} />
									<Text
										numberOfLines={2}
										style={[duelTextStyles.body, styles.flex, { fontSize: 11 * scale }]}
									>
										{`${duelText(t, 'T_SITE_DUELS_SEASON_MILESTONE')} · ${title}`}
									</Text>
								</View>
								{status ? (
									<DuelRewardStatusPill
										status={status}
										scale={scale}
										label={rewardStatusText(t, status)}
									/>
								) : null}
							</View>
						);
					})}

					<DuelButton
						label={duelText(t, 'T_SITE_DUELS_VIEW_BREAKDOWN')}
						onPress={onBreakdown}
						width={inner}
						scale={scale}
						labelSize={15}
					/>
				</>
			) : null}
		</DuelPanel>
	);
};

const DuelHistoryScreen = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigation = useNavigation<NavType>();
	const history = useSelector(selectDuelsHistory);
	const [typesOpen, setTypesOpen] = useState(false);
	const [openDuelId, setOpenDuelId] = useState<string | null>(null);
	const filter = history.filter;

	useEffect(() => {
		dispatch(duelsHistoryRequest({ tab: 'all', duelTypeId: null }));
	}, [dispatch]);

	const types = history.personalBests.filter(
		(best): best is typeof best & { duelTypeId: string } => best.duelTypeId !== null,
	);
	const selectedType = types.find(type => type.duelTypeId === filter.duelTypeId);

	const loadMore = () => {
		if (history.nextCursor && !history.loading) dispatch(duelsHistoryLoadMore());
	};

	return (
		<DuelsPage
			title={duelText(t, 'T_SITE_DUELS_HISTORY_TITLE')}
			onBack={() => navigation.goBack()}
			onEndReached={loadMore}
		>
			{({ width, scale }) => (
				<>
					<View
						style={[
							styles.row,
							styles.tabs,
							{ width, padding: 4 * scale, borderRadius: 22 * scale },
						]}
					>
						{TABS.map(({ tab, key }) => {
							const active = filter.tab === tab;
							return (
								<TouchableOpacity
									key={tab}
									accessibilityRole='tab'
									accessibilityState={{ selected: active }}
									onPress={() =>
										dispatch(duelsHistoryRequest({ tab, duelTypeId: filter.duelTypeId }))
									}
									style={[
										styles.tab,
										{ height: 34 * scale, borderRadius: 17 * scale },
										active && styles.tabActive,
									]}
								>
									<Text
										style={[
											active ? duelTextStyles.title : duelTextStyles.body,
											{ fontSize: 13 * scale },
										]}
									>
										{duelText(t, key)}
									</Text>
								</TouchableOpacity>
							);
						})}
					</View>

					<TouchableOpacity onPress={() => setTypesOpen(open => !open)} accessibilityRole='button'>
						<DuelPanel scale={scale} width={width}>
							<View style={[styles.row, styles.between]}>
								<View style={[styles.row, { columnGap: 8 * scale }]}>
									<DuelIconCardsSvg width={18 * scale} height={18 * scale} />
									<Text style={[duelTextStyles.title, { fontSize: 14 * scale }]}>
										{selectedType?.duelTypeName ?? duelText(t, 'T_SITE_DUELS_ALL_TYPES')}
									</Text>
								</View>
								<View style={{ transform: [{ rotate: typesOpen ? '-90deg' : '90deg' }] }}>
									<DuelIconChevronSvg width={16 * scale} height={16 * scale} />
								</View>
							</View>
							{typesOpen
								? [
										{ duelTypeId: null, duelTypeName: duelText(t, 'T_SITE_DUELS_ALL_TYPES') },
										...types,
									].map(type => (
										<TouchableOpacity
											key={type.duelTypeId ?? 'all'}
											onPress={() => {
												setTypesOpen(false);
												dispatch(
													duelsHistoryRequest({ tab: filter.tab, duelTypeId: type.duelTypeId }),
												);
											}}
										>
											<Text
												style={[
													type.duelTypeId === filter.duelTypeId
														? duelTextStyles.gold
														: duelTextStyles.body,
													{ fontSize: 14 * scale, paddingVertical: 6 * scale },
												]}
											>
												{type.duelTypeName ?? '—'}
											</Text>
										</TouchableOpacity>
									))
								: null}
						</DuelPanel>
					</TouchableOpacity>

					{history.items.length ? (
						<View style={[styles.row, { width, paddingHorizontal: 12 * scale }]}>
							<Text style={[duelTextStyles.muted, styles.flex, { fontSize: 11 * scale }]}>
								{duelText(t, 'T_SITE_DUELS_DATE')}
							</Text>
							<Text
								style={[
									duelTextStyles.muted,
									styles.centerText,
									{ width: 80 * scale, fontSize: 11 * scale },
								]}
							>
								{duelText(t, 'T_SITE_DUELS_SCORE')}
							</Text>
							<Text
								style={[
									duelTextStyles.muted,
									styles.centerText,
									{ width: 90 * scale, fontSize: 11 * scale },
								]}
							>
								{duelText(t, 'T_SITE_DUELS_POSITION')}
							</Text>
						</View>
					) : null}

					{history.items.map(entry => (
						<HistoryRow
							key={entry.duelId}
							entry={entry}
							width={width}
							scale={scale}
							open={openDuelId === entry.duelId}
							onToggle={() =>
								setOpenDuelId(current => (current === entry.duelId ? null : entry.duelId))
							}
							onBreakdown={() =>
								navigation.navigate('DuelScoreBreakdown', { duelId: entry.duelId })
							}
						/>
					))}

					{history.loading ? <DuelLoading /> : null}

					{!history.loading && history.errorKey ? (
						<DuelNotice
							scale={scale}
							width={width}
							text={duelText(t, history.errorKey)}
							actionLabel={duelText(t, 'T_SITE_DUELS_RETRY')}
							onAction={() =>
								history.items.length
									? dispatch(duelsHistoryLoadMore())
									: dispatch(duelsHistoryRequest(filter))
							}
						/>
					) : null}

					{!history.loading && history.loaded && !history.errorKey && !history.items.length ? (
						<DuelNotice
							scale={scale}
							width={width}
							text={duelText(t, 'T_SITE_DUELS_HISTORY_EMPTY')}
						/>
					) : null}
				</>
			)}
		</DuelsPage>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	between: {
		justifyContent: 'space-between',
	},
	flex: {
		flex: 1,
		minWidth: 0,
	},
	divider: {
		width: 1,
		backgroundColor: 'rgba(255,233,168,0.25)',
	},
	centerText: {
		textAlign: 'center',
	},
	centerRow: {
		justifyContent: 'center',
	},
	tabs: {
		backgroundColor: 'rgba(0,0,0,0.45)',
		borderWidth: 1,
		borderColor: eventColors.countdownBorder,
	},
	tab: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
	},
	tabActive: {
		backgroundColor: '#8C1A0E',
	},
});

export default DuelHistoryScreen;
